// Badge logic: tiered + themed

export const TIERS = [
  { id: "bronze", label: "Bronze", threshold: 5, color: "#A0522D" },
  { id: "silver", label: "Silver", threshold: 25, color: "#9CA3AF" },
  { id: "gold", label: "Gold", threshold: 100, color: "#B8860B" },
  { id: "platinum", label: "Platinum", threshold: 500, color: "#1A4331" },
];

export function currentTier(hours) {
  let cur = null;
  for (const t of TIERS) if (hours >= t.threshold) cur = t;
  return cur;
}

export function nextTier(hours) {
  for (const t of TIERS) if (hours < t.threshold) return t;
  return null;
}

export const THEMED = [
  { id: "first-step",      label: "First Step",       desc: "Log your first volunteering hour.",         test: (s) => s.total_hours >= 1 },
  { id: "ten-up",          label: "Ten Up",           desc: "Reach 10 approved hours.",                  test: (s) => s.total_hours >= 10 },
  { id: "community-hero",  label: "Community Hero",   desc: "Reach 50 approved hours.",                  test: (s) => s.total_hours >= 50 },
  { id: "connector",       label: "Connector",        desc: "Volunteer across 3 different organisations.", test: (s) => s.orgs_count >= 3 },
  { id: "regular",         label: "Regular",          desc: "Submit 10 hour logs.",                      test: (s) => s.logs_count >= 10 },
];

export const REFERRAL_BADGES = [
  { id: "referral_1",  label: "First Invite",     desc: "Bring 1 friend to CivicHearth.",   threshold: 1,  test: (s) => (s.referral_count || 0) >= 1 },
  { id: "referral_5",  label: "Ripple Effect",    desc: "Refer 5 volunteers who onboard.", threshold: 5,  test: (s) => (s.referral_count || 0) >= 5 },
  { id: "referral_10", label: "Community Builder", desc: "Refer 10 volunteers.",            threshold: 10, test: (s) => (s.referral_count || 0) >= 10 },
  { id: "referral_15", label: "Movement Maker",   desc: "Refer 15 volunteers.",             threshold: 15, test: (s) => (s.referral_count || 0) >= 15 },
  { id: "referral_20", label: "Neighbourhood Champion", desc: "Refer 20 volunteers.",       threshold: 20, test: (s) => (s.referral_count || 0) >= 20 },
];

export function earnedReferral(stats) {
  return REFERRAL_BADGES.map(b => ({ ...b, earned: b.test(stats) }));
}

export function earnedThemed(stats) {
  return THEMED.map(b => ({ ...b, earned: b.test(stats) }));
}

export function progressToNext(hours) {
  const nxt = nextTier(hours);
  if (!nxt) return { pct: 100, next: null };
  const prev = currentTier(hours)?.threshold || 0;
  const span = nxt.threshold - prev;
  const into = hours - prev;
  return { pct: Math.min(100, Math.round((into / span) * 100)), next: nxt };
}

// Dashboard "so close" nudge: fires when the volunteer has started logging
// (hours > 0) and is within 10% of the next tier's threshold — proportionate,
// so Gold (100h) nudges from 90h while Bronze (5h) waits until 4.5h. Prefers
// the smallest matched/recommended opportunity whose required hours close the
// gap in one go; pick is null when nothing fits (caller falls back to browse).
export const NUDGE_WINDOW_PCT = 0.10;

export function tierNudgeState(hours, opps = []) {
  const h = hours || 0;
  if (h <= 0) return null;
  const next = nextTier(h);
  if (!next) return null;
  const remaining = Math.round((next.threshold - h) * 10) / 10;
  if (remaining > next.threshold * NUDGE_WINDOW_PCT) return null;
  const pick = opps
    .filter((o) => o && o.id && (o.hours_required || 0) >= remaining)
    .sort((a, b) => a.hours_required - b.hours_required)[0] || null;
  return { remaining, next, pick };
}

// Dashboard "next tier" progress ring. Pure + exported so the edge cases
// (platinum top-tier, sub-bronze, zero hours) are unit-testable without UI.
export function tierRingState(hours) {
  const h = Math.max(0, hours || 0);
  const tier = currentTier(h);
  const { pct, next } = progressToNext(h);
  const fmt = (v) => String(Math.round(v * 10) / 10);
  if (!next) {
    // Platinum: complete ring, top-tier state instead of a next target
    return { pct: 100, color: tier.color, tier, next: null,
             short: "Top tier", label: "Top tier — every hour still counts" };
  }
  const remaining = Math.max(0, next.threshold - h);
  const short = `${fmt(remaining)}h to ${next.label}`;
  if (!tier) {
    if (h === 0) {
      // zero hours: encouraging empty ring, never nothing
      return { pct: 0, color: next.color, tier: null, next, short,
               label: `Your ring starts with your first approved hour — ${short}` };
    }
    return { pct, color: next.color, tier: null, next, short,
             label: pct >= 50 ? `${short} — nearly there` : short };
  }
  return { pct, color: next.color, tier, next, short, label: short };
}
